import type { BaseColumn } from '@konnecta/generic-table';
import { Button, Space, Tag, Typography } from 'antd';
import { TFunction } from 'i18next';
import type { InventoryItemRow } from '../../types';
import { createdAtColumn } from '@app/components/common/createdAtColumn';

export interface InventoryTableHandlers {
  onEdit: (row: InventoryItemRow) => void;
  onDelete: (row: InventoryItemRow) => void;
  onRecordMovement: (row: InventoryItemRow) => void;
}

/** Small capability tags (Consumable / Sellable / Rentable). */
const renderCapabilities = (row: InventoryItemRow, t: TFunction) => {
  const tags: React.ReactNode[] = [];
  if (row.isConsumable) tags.push(<Tag key="c">{t('inventory-cap-consumable')}</Tag>);
  if (row.isSellable) tags.push(<Tag key="s">{t('inventory-cap-sellable')}</Tag>);
  if (row.isRentable) tags.push(<Tag key="r">{t('inventory-cap-rentable')}</Tag>);
  return tags.length ? <Space size={[4, 4]}>{tags}</Space> : <Typography>-</Typography>;
};

export const getInventoryItemsTableColumns = (
  t: TFunction,
  handlers: InventoryTableHandlers
): BaseColumn<InventoryItemRow>[] => [
  {
    id: 'name',
    header: t('inventory-col-name'),
    accessor: 'name',
    width: 240,
    sortable: false,
    copy: false,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
  {
    id: 'category',
    header: t('inventory-col-category'),
    accessor: 'categoryName',
    width: 180,
    copy: false,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
  {
    id: 'capabilities',
    header: t('inventory-col-capabilities'),
    accessor: 'itemId',
    width: 220,
    sortable: false,
    filterable: false,
    copy: false,
    cellRenderer: (_value, row) => renderCapabilities(row, t),
  },
  {
    id: 'unit',
    header: t('inventory-col-unit'),
    accessor: 'unit',
    width: 110,
    copy: false,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
  {
    id: 'onHand',
    header: t('inventory-col-on-hand'),
    accessor: 'onHand',
    align: 'right',
    width: 160,
    sortable: false,
    copy: false,
    cellRenderer: (value, row) => (
      <div className="flex w-full items-center justify-end gap-2">
        {row.lowStock && <Tag color="warning">{t('inventory-low-stock')}</Tag>}
        <Typography className="tabular-nums" style={{ fontWeight: 600 }}>
          {value as number}
        </Typography>
      </div>
    ),
  },
  createdAtColumn<InventoryItemRow>(t),
  {
    id: 'actions',
    header: t('inventory-col-actions'),
    accessor: 'itemId',
    align: 'center',
    sticky: 'right',
    width: 280,
    sortable: false,
    filterable: false,
    copy: false,
    cellRenderer: (_value, row) => (
      <Space size={0}>
        <Button type="link" size="small" onClick={() => handlers.onRecordMovement(row)}>
          {t('inventory-record-movement')}
        </Button>
        <Button type="link" size="small" onClick={() => handlers.onEdit(row)}>
          {t('inventory-action-edit')}
        </Button>
        <Button type="link" size="small" danger onClick={() => handlers.onDelete(row)}>
          {t('inventory-action-delete')}
        </Button>
      </Space>
    ),
  },
];
